<template>

  <v-sheet
    ref         = "video_container"
    @mouseover  = "isMouseOverPane = true;"
    @mousemove  = "onMouseMove"
    @mouseout   = "isMouseOverPane = false;"
  >

    <div class="video_container">

      <VideoContainer
        class   = "video"
      >
      </VideoContainer>

      <!-- File Wizard -->
      <SelectFileWizard
        v-if    = "showFileWizard"
        class   = "wizard"
      >
      </SelectFileWizard>

      <!-- Loading Wizard -->
      <LoadingWizard
        v-if    = "showLoadingWizard"
        class   = "wizard"
      >
      </LoadingWizard>

      <!-- Error Wizard -->
      <ErrorWizard
        v-if    = "showErrorWizard"
        class   = "wizard"
      >
      </ErrorWizard>

      <!-- Current Timecode -->
      <div
        v-if         = "video.canPlay && video.showTimecodeOverlay && height > 100"
        class        = "timecode_overlay"
        @contextmenu = "onTimeClick"
      >
        <div
          v-if  = "timeDisplaySource && (timeDisplaySource.type === timecodeDisplayType) && timeDisplaySource.name !== nptName()"
          class = "timecode"
        >
          {{ currentTimecode }}<span class="framerate">@{{ video.framerate }}</span>
        </div>
        <div
          v-if  = "timeDisplaySource && (timeDisplaySource.type === timecodeDisplayType) && timeDisplaySource.name === nptName()"
          class = "timecode"
        >
          {{ npt }}
        </div>
        <div
          v-if  = "timeDisplaySource && (timeDisplaySource.type === secondsDisplayType)"
          class = "timecode"
        >
          {{ time }}
        </div>
        <div
          v-if  = "timeDisplaySource && (timeDisplaySource.type === framesDisplayType)"
          class = "timecode"
        >
          {{ frames }}
        </div>
      </div>

      <!-- Scrubber -->
      <ScrubBar
        v-if    = "height > 0"
        class   = "scrubber"
        :class  = "{ hidden: hideScrubber }"
      >
      </ScrubBar>

    </div>

    <VideoControls 
      class   = "controls"
      :class  = "{ hidden: hideControls }"
    >
    </VideoControls>

  </v-sheet>

</template>

<script lang="js">

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import ErrorWizard from './components/wizards/ErrorWizard.vue';
import ScrubBar from './components/ScrubBar';
import VideoContainer from './components/VideoContainer';
import VideoControls from './components/VideoControls';
import LoadingWizard from './components/wizards/LoadingWizard';
import SelectFileWizard from './components/wizards/SelectFileWizard';
import { ATTACH_FULLSCREEN_ELEMENT } from '@/utils/action-types';
import { splitTime } from '@/utils/time-utilities';
import {LEVELS, sendEphemeralSnack} from "@/store/utilities/snacks.utils";
import {NPT_TIMECODE_SOURCE} from "@/utils/constants";
import {nptToTime} from "@/store/utilities/timecodes.utils";

// #endregion Imports

// #region Constants

const FULLSCREEN_HIDE_DELAY_MS    = 3000;

// #endregion Constants

export default {

  // #region Properties

  name: 'VideoPane',
  data() {
    return {
      isMouseOverPane:     false,
      cursorWindowY:       0,
      cursorMovedRecently: false,
      cursorMoveHandle:    0,
      secondsDisplayType:  'seconds',
      timecodeDisplayType: 'timecode',
      framesDisplayType:   'frames',

      sizeObserver: undefined,
      height:       undefined
    }
  },
  computed: {
    ...mapState([
      'video',
      'videoController',
      'videoPath',
      'timeDisplaySource',
      'videoCurrentTime'
    ]),
    showFileWizard() {
      return !this.videoPath && !this.video.isLoading;
    },
    showLoadingWizard() {
      return this.video.isLoading;
    },
    showErrorWizard() {
      return this.video.hasFailedLoading;
    },
    hideScrubber() {
      // Scrubber is only shown when cursor is over the pane outside fullscreen.
      if (!this.video.isFullscreen) {
        return (!this.isMouseOverPane || !this.video.canPlay);
      }

      return !(this.isCursorInFullscreenHotzone || this.cursorMovedRecently);
    },
    hideControls() {
      // Controls are always visible outside of fullscreen.
      if (!this.video.isFullscreen) {
        return false;
      }

      return !(this.isCursorInFullscreenHotzone || this.cursorMovedRecently);
    },
    isCursorInFullscreenHotzone() {
      return (this.cursorWindowY > (window.innerHeight - 100));
    },
    currentTimecode() {
      if (!this.video.currentTimecode) {
        return undefined;
      }

      return this.video.currentTimecode.split('@')[0];
    },
    time() {
      let time = splitTime(this.videoCurrentTime);
      return `${time[0]}:${time[1]}:${time[2]}.${time[3]}`
    },
    frames() {
      return this.video.currentFrame;
    },
    wizardMessage() {
      if (this.video.isLoading) {
        return 'Loading...';
      } else {
        return 'Browse or select a recently opened file.';
      }
    },
    mediaHasVideo() {
      return !!this.videoController?.mediaProperties?.videoProperties[0];
    },
    npt() {
      let npt =  nptToTime(this.video.currentTimecode);
      let time = splitTime(npt);
      return `${time[0]}:${time[1]}:${time[2]}.${time[3]}`
    },
  },

  components: {
    LoadingWizard,
    ScrubBar,
    SelectFileWizard,
    VideoControls,
    VideoContainer,
    ErrorWizard
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.$store.dispatch(ATTACH_FULLSCREEN_ELEMENT, this.$refs.video_container.$el);

    /*
     * We watch the height of the container here to hide certain elements when
     * this pane gets resized to a point where they may stick out. The easiest thing
     * to do would be to set `overflow: hidden` on the parent container, but there
     * are elements we want to stick out.
     */
    this.sizeObserver = new ResizeObserver(this.onResize).observe(this.$refs.video_container.$el);
    this.height = this.$refs.video_container.$el.clientHeight;
  },

  beforeDestroy() {
    if (this.sizeObserver) {
      this.sizeObserver.unobserve(this.$refs.video_container.$el);
    }
  },

  // #endregion Life-Cycle

  // #region Methods

  methods: {

    // #region Events

    onResize() {
      if (!this.$refs.video_container) {
        this.height = 0;
        return;
      }

      this.height = this.$refs.video_container.$el.clientHeight;
    },

    onMouseMove(mouseEvent) {
      this.cursorWindowY = mouseEvent.screenY;
      
      if (this.cursorMoveHandle) {
        clearTimeout(this.cursorMoveHandle);
      }

      this.cursorMovedRecently = true;
      this.cursorMoveHandle = setTimeout(
        () => this.cursorMovedRecently = false, 
        FULLSCREEN_HIDE_DELAY_MS
      );
    },

    onTimeClick(event){
      event.preventDefault();
      let timeSourceDisplay;
      let message;
      if (this.timeDisplaySource && this.timeDisplaySource.type === 'timecode' && this.timeDisplaySource.name !== this.nptName()){
        timeSourceDisplay = this.currentTimecode + '@' + this.video.framerate
        message = `Timecode ${timeSourceDisplay} copied to clipboard.`
      }
      else if (this.timeDisplaySource && this.timeDisplaySource.type === 'timecode' && this.timeDisplaySource.name === this.nptName()){
        timeSourceDisplay = this.npt;
        message = `Normal play time ${this.npt} copied to clipboard.`
      }
      else if (this.timeDisplaySource && this.timeDisplaySource.type === 'seconds'){
        timeSourceDisplay = this.time;
        message = `Time ${this.time} copied to clipboard.`
      }
      else if (this.timeDisplaySource && this.timeDisplaySource.type === 'frames'){
        timeSourceDisplay = this.frames;
        message = `Frame number ${this.frames} copied to clipboard.`
      }
      navigator.clipboard.writeText(timeSourceDisplay)
        .then(() => {
          sendEphemeralSnack(this.$store, LEVELS.INFO, message);
        })
        .catch(() => {
          sendEphemeralSnack(this.$store, LEVELS.ERROR, 'Failed to copy timeSourceDisplay');
        });
    },

    nptName() {
      return NPT_TIMECODE_SOURCE.name;
    },

    // #endregion Events

  }

  // #endregion Methods

}

</script>

<style scoped lang="scss">

.v-sheet {
  display:        flex;
  flex-direction: column;
  position:       relative;

  .video_container {
    flex:           1;
    position:       relative;

    .wizard,
    .video {
      position:       absolute;
      top:            0;
      bottom:         0;
      left:           0;
      right:          0;
    }

    .timecode_overlay {
      position:       absolute;
      top:            10px;
      left:           0;
      right:          0;

      display:          flex;
      justify-content:  center;

      .timecode {
        min-width:      170px;
        padding:        0px 8px;
        background:     rgba(black, 0.7);

        border:         1px solid rgba(white, 0.3);
        border-radius:  4px;

        font-family:    'Source Sans Pro';
        font-size:      1.5rem;
        text-align:     center;

        .framerate {
          color:          rgba(255, 255, 255, 0.5);
        }
      }
    }

    .scrubber {
      position:       absolute;
      left:           0;
      right:          0;
      bottom:         0;
    }
  }

  .timecode_overlay,
  .overlays,
  .controls {
    overflow:       hidden;
  }

  .scrubber,
  .controls {
    opacity:        1;
    transition:     opacity .2s;
    &.hidden {
      opacity:        0;
    }
  }

  &:fullscreen {
    .scrubber {
      bottom:         52px;
    }

    .controls {
      position:       absolute;
      bottom:         0;
      left:           0;
      right:          0;

      background-color: rgba(black, 0.6);

      overflow:       visible;
    }
  }
}

</style>
